import { AddCircleOutline, SearchOutlined } from '@mui/icons-material'
import Button from '@mui/material/Button'
import DownloadIcon from '@mui/icons-material/Download'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { PermissionAccess, PermissionKey, PermissionProvider } from '../../../../context'
import { resetVisitedId } from '../../../../store/reducer/ad-management'
import { TADMembership } from '../../../../type'
import { dateFormat, Format } from '../../../../utility/date-format'
import { PATH } from '../../../../utility/path'
import { Preloader } from '../../../common'
import { ArchiveButton, CustomPagination, CustomTableCell, EditButton, ViewButton } from '../../../shared'
import { MembershipGroupNameDialogButton, MembershipGroupNameDialogView } from '../membership'
import { useGroupSearchable } from '../../../../hooks'
import { Box, Typography } from '@mui/material'
import { MembershipType } from '../../../../view/admin/ad-management/Memberships'
import { TADMembershipTemplate } from '../../../../type/ad-management'
import { ConfirmationAlert } from '../../../shared/alerts'
import { API } from '../../../../api'
import { usePermission } from '../../../../context/Permission'

type TMembershipListProps = {
  membershipType: MembershipType
  memberships: TADMembership[] | TADMembershipTemplate[]
  setSearch: (search: string) => void
  setMembership: (membership: TADMembership | TADMembershipTemplate) => void
  setIsMembershipView: (isMembershipView: boolean) => void
  totalCount: number
  page: number
  maxPageSize: number
  setPage: (page: number) => void
  isLoading: boolean
  handleExportMembershipButtonClick: () => void
  getMemberships?: () => void
}

export const MembershipList: React.FC<TMembershipListProps> = ({
  membershipType,
  memberships,
  setSearch,
  setMembership,
  setIsMembershipView,
  totalCount,
  page,
  maxPageSize,
  setPage,
  isLoading,
  handleExportMembershipButtonClick,
  getMemberships,
}) => {
  const navigation = useNavigate()
  const dispatch = useDispatch()
  const hasDetailsPermission = usePermission(PermissionKey.btns_users)

  const [membershipToDelete, setMembershipToDelete] = useState<string | null>(null)
  const [isConfirmDelete, setIsConfirmDelete] = useState(false)
  const { searchableText, setSearchableText, isShow, groupDialogDetailTitle, onCancel, setGroupDialogDetailTitle } = useGroupSearchable()
  const [isLoadingArchive, setIsLoadingArchive] = useState(false)
  const [isConfirmModal, setIsConfirmModal] = useState(false)
  const [archiveMembership, setArchiveMembership] = useState<string>('')

  const handleDeleteConfirm = async () => {
    try {
      if (membershipToDelete) {
        await API.ADManagement.membership.deleteTemplate(membershipToDelete)
      }
    } catch (err) {
      console.error('Failed to delete membership template:', err)
    } finally {
      getMemberships && getMemberships()
      setIsConfirmDelete(false)
    }
  }
  const handleDeleteCancel = async () => {
    setIsConfirmDelete(false)
  }

  const handleArchiveMembership = async () => {
    try {
      setIsLoadingArchive(true)
      await API.ADManagement.membership.delete(archiveMembership || '', membershipType === MembershipType.External)
      setIsLoadingArchive(false)
      setIsConfirmModal(false)
      getMemberships && getMemberships()
    } catch (err) {
      console.log('Error archiving membership:', err)
    } finally {
      setArchiveMembership('')
    }
  }

  const resetVisitedFlow = useCallback(() => {
    dispatch(resetVisitedId())
  }, [dispatch])

  useEffect(() => {
    void resetVisitedFlow()
  }, [resetVisitedFlow])

  const renderPreloader = () => (
    <Grid
      container
      justifyContent='center'
    >
      <Preloader size='sm' />
    </Grid>
  )

  const renderToolbar = () => {
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setSearch((event.target as HTMLInputElement).value)
        setPage(1)
      }
    }

    const handleCreateMembership = () => {
      navigation(`${PATH.admin.ad_management.membership}/new?type=${membershipType}`)
    }

    const renderExportMembershipsButton = () => (
      <Button
        variant='outlined'
        startIcon={<DownloadIcon />}
        onClick={handleExportMembershipButtonClick}
        data-testid='membership-export'
        disabled={memberships.length === 0 || isLoading}
        className={'h-full'}
      >
        Export
      </Button>
    )

    return (
      <Grid
        container
        spacing={2}
        sx={{ pb: 3 }}
      >
        <Grid
          item
          xs={4}
        >
          <TextField
            label='Search'
            size='small'
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'data-testid': `${membershipType === MembershipType.Template ? 'template' : 'membership'}-search`,
            }}
            onKeyDown={handleKeyDown}
          />
        </Grid>
        <Grid
          container
          item
          xs={2}
          justifyContent='flex-start'
        >
          {membershipType !== MembershipType.Template && renderExportMembershipsButton()}
        </Grid>
        <Grid
          container
          item
          xs={6}
          justifyContent='flex-end'
        >
          <PermissionProvider permission={PermissionKey.btns_memberships}>
            <Button
              variant='contained'
              startIcon={<AddCircleOutline />}
              onClick={handleCreateMembership}
              data-testid={`${membershipType === MembershipType.Template ? 'template' : 'membership'}-create`}
              className={'h-full'}
            >
              Create {membershipType === MembershipType.Template ? 'Template' : 'Membership'}
            </Button>
          </PermissionProvider>
        </Grid>
      </Grid>
    )
  }

  const renderCoveredEntities = () => {
    const isTemplate = membershipType === MembershipType.Template

    const renderHead = () => (
      <TableHead>
        <TableRow className='bg-base300'>
          {isTemplate ? (
            <>
              <TableCell size={'small'}>
                <span className='t-w-800'>Name</span>
              </TableCell>
              <TableCell>
                <span className='t-w-800'>Created At</span>
              </TableCell>
              <TableCell>
                <span className='t-w-800'>Updated At</span>
              </TableCell>
              <TableCell />
            </>
          ) : (
            <>
              <TableCell size={'small'}>
                <span className='t-w-800'>Membership</span>
              </TableCell>
              <TableCell>
                <span className='t-w-800'>Description</span>
              </TableCell>
              <TableCell>
                <span className='t-w-800'>Covered Entities</span>
              </TableCell>
              <TableCell>
                <span className='t-w-800'>Members</span>
              </TableCell>
              <TableCell>
                <span className='t-w-800'>Updated At</span>
              </TableCell>
              <TableCell />
            </>
          )}
        </TableRow>
      </TableHead>
    )

    const renderBody = () => {
      if (isTemplate) {
        const handleEdit = ({ _id }: TADMembershipTemplate) => {
          navigation(`${PATH.admin.ad_management.membership}/${_id}?type=${membershipType}`)
        }

        const handleView = (m: TADMembershipTemplate) => {
          setMembership(m)
          setIsMembershipView(true)
        }
        const handleDelete = (m: TADMembershipTemplate) => {
          setMembershipToDelete(m._id)
          setIsConfirmDelete(true)
        }

        return (
          <TableBody>
            {(memberships as TADMembershipTemplate[]).map((membership, index) => (
              <TableRow key={membership._id || index}>
                <CustomTableCell
                  maxWidth={'200px'}
                  dataTest={`template-${index}-name`}
                  size={'small'}
                >
                  {membership.name}
                </CustomTableCell>
                <CustomTableCell
                  dataTest={`template-${index}-createdAt`}
                  maxWidth='200px'
                >
                  {dateFormat(membership.created_at, Format['DD MMM YYYY'])} at {dateFormat(membership.created_at, Format['HH MM'])}
                </CustomTableCell>
                <CustomTableCell
                  dataTest={`template-${index}-updatedAt`}
                  maxWidth='200px'
                >
                  {dateFormat(membership.updated_at, Format['DD MMM YYYY'])} at {dateFormat(membership.updated_at, Format['HH MM'])}
                </CustomTableCell>
                <CustomTableCell
                  align='right'
                  maxWidth='200px'
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    <ViewButton
                      onClick={() => handleView(membership)}
                      dataTestId={`template-${index}-view`}
                    />
                    <PermissionProvider
                      permission={PermissionKey.btns_memberships}
                      className='d-ib'
                    >
                      <EditButton
                        onClick={() => handleEdit(membership)}
                        dataTestId={`template-${index}-edit`}
                      />
                    </PermissionProvider>
                    <PermissionProvider
                      permission={PermissionKey.btns_memberships}
                      className='d-ib'
                    >
                      <ArchiveButton
                        onClick={() => handleDelete(membership)}
                        dataTestId={`template-${index}-archive`}
                        disabled={hasDetailsPermission === PermissionAccess.Read}
                      />
                    </PermissionProvider>
                  </Box>
                </CustomTableCell>
              </TableRow>
            ))}
          </TableBody>
        )
      } else {
        const handleEdit = ({ GroupName }: TADMembership) => {
          navigation(`${PATH.admin.ad_management.membership}/${GroupName}?type=${membershipType}`)
        }
        const handleArchive = (membership: TADMembership) => {
          setArchiveMembership(membership.GroupName)
          setIsConfirmModal(true)
        }
        const handleView = (m: TADMembership) => {
          setMembership(m)
          setIsMembershipView(true)
        }

        return (
          <TableBody>
            {(memberships as TADMembership[]).map((membership, index) => (
              <TableRow key={index}>
                <CustomTableCell
                  maxWidth={'150px'}
                  dataTest={`membership-${index}-id`}
                  size={'small'}
                >
                  <MembershipGroupNameDialogButton
                    label={membership.GroupName}
                    setSearchableText={setSearchableText}
                    searchableText={membership.GroupName}
                  />
                </CustomTableCell>
                <CustomTableCell
                  dataTest={`membership-${index}-description`}
                  maxWidth='200px'
                >
                  {membership.Description}
                </CustomTableCell>
                <CustomTableCell
                  dataTest={`membership-${index}-name`}
                  maxWidth='150px'
                >
                  {membership.GroupMemberships?.join('; ')}
                </CustomTableCell>
                <CustomTableCell
                  dataTest={`membership-${index}-members`}
                  maxWidth='50px'
                >
                  {membership.MembersCount}
                </CustomTableCell>
                <CustomTableCell
                  dataTest={`membership-${index}-date`}
                  maxWidth='150px'
                >
                  {dateFormat(membership.WhenChanged, Format['DD MMM YYYY'])} at {dateFormat(membership.WhenChanged, Format['HH MM'])}
                </CustomTableCell>
                <CustomTableCell
                  align='right'
                  sx={{ width: '120px', minWidth: '120px' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    <ViewButton
                      onClick={() => handleView(membership)}
                      dataTestId={`membership-${index}-view`}
                    />
                    <PermissionProvider
                      permission={PermissionKey.btns_memberships}
                      className='d-ib'
                    >
                      <EditButton
                        onClick={() => handleEdit(membership)}
                        dataTestId={`membership-${index}-edit`}
                      />
                    </PermissionProvider>
                    <PermissionProvider
                      permission={PermissionKey.btns_memberships}
                      className='d-ib'
                    >
                      <ArchiveButton
                        onClick={() => handleArchive(membership)}
                        dataTestId={`membership-${index}-archive`}
                        disabled={hasDetailsPermission === PermissionAccess.Read}
                      />
                    </PermissionProvider>
                  </Box>
                </CustomTableCell>
              </TableRow>
            ))}
          </TableBody>
        )
      }
    }

    const renderPagination = () => {
      const handlePagination = (event: React.ChangeEvent<unknown>, page_number: number) => {
        setPage(page_number)
      }
      return (
        <div style={{ paddingTop: '30px', justifyContent: 'center', display: 'flex' }}>
          <CustomPagination
            total_count={totalCount}
            max_page_size={maxPageSize}
            page_number={page}
            showFirstButton={false}
            showLastButton={false}
            handlePagination={handlePagination}
          />
        </div>
      )
    }
    if (memberships && memberships.length > 0) {
      return (
        <>
          <TableContainer>
            <Table>
              {renderHead()}
              {renderBody()}
            </Table>
          </TableContainer>
          {renderPagination()}
        </>
      )
    } else {
      return (
        <div
          data-testid={`no-${membershipType === MembershipType.Template ? 'templates' : 'memberships'}`}
          style={{ textAlign: 'center', fontWeight: 'bold', color: 'var(--pantoneBlue)' }}
        >
          No Results
        </div>
      )
    }
  }

  return (
    <>
      {renderToolbar()}
      {isLoading ? renderPreloader() : renderCoveredEntities()}
      <MembershipGroupNameDialogView
        isShow={isShow}
        searchableText={searchableText}
        setSearchableText={setSearchableText}
        onCancel={onCancel}
        setTitle={setGroupDialogDetailTitle}
        title={groupDialogDetailTitle}
      />
      <ConfirmationAlert
        dataTestId='template-delete-confirmation'
        isShow={isConfirmDelete}
        shouldAlignBetween
        content={
          <>
            <Typography
              fontWeight={'bold'}
              variant={'h6'}
              fontSize={'20px'}
            >
              Are you sure you want to delete this template?
            </Typography>
          </>
        }
        confirmationLabel='Delete'
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        onClose={handleDeleteCancel}
      />
      <ConfirmationAlert
        isShow={isConfirmModal}
        title={
          <Typography
            fontWeight={'bold'}
            variant={'h6'}
            fontSize={'20px'}
            data-testid={'ce-action'}
          >
            Are you sure you want to delete this AD Group?
          </Typography>
        }
        content={
          <Typography data-testid={'ce-action'}>
            <strong>Please Note!</strong> Deleting this Active directory group will permanently remove all permissions and access rights
            granted through it
          </Typography>
        }
        dataTestId='confirmation'
        onClose={() => {
          setIsConfirmModal(false)
          setArchiveMembership('')
        }}
        onCancel={() => {
          setIsConfirmModal(false)
          setArchiveMembership('')
        }}
        onConfirm={handleArchiveMembership}
        cancellationLabel='Cancel'
        confirmationLabel='Confirm'
        isLoading={isLoadingArchive}
        shouldAlignBetween
      />
    </>
  )
}
